import { Link, useNavigate, useParams } from 'react-router-dom';
import PageList from '../../templates/PageList';
import BadgeStatus from '../../atoms/BadgeStatus';
import { useMemo } from 'react';
import appConfig from '../../../config/app-config';

type Operation = {
    id: string;
    operationCode: string;
    assignorName: string;
    assignorCnpj: string;
    status: string;
    totalDocuments: number;
    totalReceivables: number;
    totalAmount: number;
    processingDate: string;
    updatedDate: string;
    notes: string;
    canEdit: boolean;
    canRemove: boolean;
    canViewDetail: boolean;
    canDownload: boolean;
};

const { address: API_URL, key: API_KEY } = appConfig.apis.convertXmlApiEnv;

const OperationPageList = () => {
    const navigate = useNavigate();
    const { page = '0', pageSize = '100' } = useParams();

    const url = `${API_URL}/operations?page=${page}&pageSize=${pageSize}`;

    const fetchHeaders = useMemo(() =>
        [
            { key: 'x-api-key', value: API_KEY || '' },
            { key: 'Accept-Charset', value: 'utf-8' }
        ],
        [API_KEY]
    );

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'green';
            case 'processing': return 'yellow';
            case 'failed': return 'red';
            default: return 'gray';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed': return 'Concluído';
            case 'processing': return 'Processando';
            case 'failed': return 'Falhou';
            default: return status;
        }
    };

    const tableHeaders = useMemo(() => [
        { id: 'operationCode', title: 'Código', canSort: false, field: 'operationCode' },
        { id: 'assignorName', title: 'Cedente', canSort: false, field: 'assignorName' },
        { id: 'totalDocuments', title: 'Documentos', canSort: false, field: 'totalDocuments' },
        { id: 'totalReceivables', title: 'Recebíveis', canSort: false, field: 'totalReceivables' },
        { id: 'totalAmount', title: 'Valor Total', canSort: false, field: 'totalAmount' },
        { id: 'processingDate', title: 'Processamento', canSort: false, field: 'processingDate' },
        { id: 'status', title: 'Status', canSort: false, field: 'status' },
        { id: 'actions', title: 'Ações', canSort: false }
    ], []);

    const renderRows = (operations: any) => {
        if (!operations || !Array.isArray(operations)) {
            return [];
        }

        return operations.map((operation: Operation, index: number) => (
            <tr key={operation.id} className={`${index % 2 === 0 ? 'bg-slate-100' : ''} hover:bg-slate-300 hover:shadow-md border-b border border-gray-300`}>
                <td className="py-2 px-2 whitespace-nowrap">{operation.operationCode || 'N/A'}</td>
                <td className="py-2 px-2 whitespace-nowrap">{operation.assignorName || 'N/A'}</td>
                <td className="py-2 px-2 whitespace-nowrap">{operation.totalDocuments || 0}</td>
                <td className="py-2 px-2 whitespace-nowrap">{operation.totalReceivables || 0}</td>
                <td className="py-2 px-2 whitespace-nowrap">{formatCurrency(operation.totalAmount || 0)}</td>
                <td className="py-2 px-2 whitespace-nowrap">{formatDate(operation.processingDate)}</td>
                <td className="py-2 px-2 whitespace-nowrap">
                    <BadgeStatus
                        color={getStatusColor(operation.status)}
                        text={getStatusLabel(operation.status)}
                    />
                </td>
                <td className="py-2 px-2 text-center whitespace-nowrap">
                    <button
                        onClick={() => navigate(`/structuring/operation/view/${operation.id}`)}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm whitespace-nowrap"
                    >
                        Gerenciar
                    </button>
                </td>
            </tr>
        ));
    };

    const pageChanged = (newPage: number) => {
        navigate(`/structuring/operations/${newPage}/${pageSize}`, { replace: true });
    };

    const pageSizeChanged = (newPageSize: number) => {
        navigate(`/structuring/operations/0/${newPageSize}`, { replace: true });
    };

    return (
        <PageList
            pageTitle="Lista de Operações"
            url={url}
            fetchHeaders={fetchHeaders}
            breadcrumb={[{ label: 'Operações', url: '#' }]}
            tableHeaders={tableHeaders}
            defaultBackendHeader={false}
            tableFieldSort=""
            tableOrderSort="ASC"
            onTableSort={() => {}}
            renderRows={renderRows}
            page={parseInt(page) || 0}
            pageSize={parseInt(pageSize) || 100}
            onPageChange={pageChanged}
            onItemsPerPageChange={pageSizeChanged}
            itemsLabel="operações"
        >
            {{
                filterSlot: null,
                controlSlot: (
                    <div className='pt-4'>
                        <Link to="/structuring/operation">
                            <button
                                type='button'
                                className='text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2'
                            >
                                Nova Operação
                            </button>
                        </Link>
                    </div>
                )
            }}
        </PageList>
    );
};

export default OperationPageList;
